<script setup>
import { computed, ref, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { WhatsAppTemplateDocument } from 'dashboard/services/WhatsAppTemplateDocument';

const props = defineProps({
  templates: { type: Array, default: () => [] },
  isRefreshing: { type: Boolean, default: false },
  showRefresh: { type: Boolean, default: false },
  initialLimit: { type: Number, default: 18 },
});

const emit = defineEmits(['select', 'refresh']);

const query = ref('');
const selectedCategory = ref('all');
const selectedLanguage = ref('all');
const visibleCount = ref(props.initialLimit);

const normalize = value =>
  String(value || '')
    .trim()
    .toLowerCase();
const titleize = value =>
  String(value || 'Unknown')
    .replaceAll('_', ' ')
    .toLowerCase()
    .replace(/\b\w/g, character => character.toUpperCase());

const templateDocument = template =>
  WhatsAppTemplateDocument.fromTemplate(template);
const getTemplateHeader = template => templateDocument(template).header;
const getTemplateBody = template => templateDocument(template).bodyText;
const getTemplateFooter = template => templateDocument(template).footerText;
const getTemplateButtons = template => templateDocument(template).buttons;

const getMediaPreview = template => {
  const header = getTemplateHeader(template);
  return header?.example?.header_handle?.[0] || '';
};

const isMediaHeader = template => templateDocument(template).hasMediaHeader;

const mediaHeaderLabel = template =>
  `${titleize(getTemplateHeader(template)?.format)} header`;

const escapeHtml = value =>
  String(value || '').replace(
    /[&<>"']/g,
    character =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;',
      })[character]
  );

const previewHtml = template =>
  escapeHtml(getTemplateBody(template)).replace(
    /({{[^}]+}})/g,
    '<span class="wa-template-variable">$1</span>'
  );

const templateSearchText = template => templateDocument(template).searchText;

const categoryOptions = computed(() => {
  const counts = props.templates.reduce((result, template) => {
    const value = normalize(template.category) || 'unknown';
    result[value] = (result[value] || 0) + 1;
    return result;
  }, {});
  const preferredOrder = ['utility', 'marketing', 'authentication'];
  const values = Object.keys(counts).sort((left, right) => {
    const leftIndex = preferredOrder.indexOf(left);
    const rightIndex = preferredOrder.indexOf(right);
    if (leftIndex !== -1 || rightIndex !== -1) {
      return (
        (leftIndex === -1 ? 99 : leftIndex) -
        (rightIndex === -1 ? 99 : rightIndex)
      );
    }
    return left.localeCompare(right);
  });
  return [
    { value: 'all', label: 'All templates', count: props.templates.length },
    ...values.map(value => ({
      value,
      label: titleize(value),
      count: counts[value],
    })),
  ];
});

const languageOptions = computed(() => {
  const counts = props.templates.reduce((result, template) => {
    const value = normalize(template.language) || 'unknown';
    result[value] = (result[value] || 0) + 1;
    return result;
  }, {});
  return Object.keys(counts)
    .sort()
    .map(value => ({ value, label: value, count: counts[value] }));
});

const filteredTemplates = computed(() => {
  const search = query.value.trim().toLowerCase();
  return props.templates.filter(template => {
    const categoryMatches =
      selectedCategory.value === 'all' ||
      normalize(template.category) === selectedCategory.value;
    const languageMatches =
      selectedLanguage.value === 'all' ||
      normalize(template.language) === selectedLanguage.value;
    const searchMatches =
      !search || templateSearchText(template).includes(search);
    return categoryMatches && languageMatches && searchMatches;
  });
});

const visibleTemplates = computed(() =>
  filteredTemplates.value.slice(0, visibleCount.value)
);

const resultLabel = computed(() => {
  if (!filteredTemplates.value.length) return 'No matching templates';
  return `Showing ${visibleTemplates.value.length} of ${filteredTemplates.value.length} templates`;
});

const categoryClass = category => {
  const value = normalize(category);
  if (value === 'utility') return 'is-utility';
  if (value === 'authentication') return 'is-authentication';
  if (value === 'marketing') return 'is-marketing';
  return 'is-default';
};

const buttonIcon = type => {
  if (type === 'PHONE_NUMBER') return 'i-lucide-phone';
  if (type === 'QUICK_REPLY') return 'i-lucide-reply';
  return 'i-lucide-external-link';
};

const selectTemplate = template => emit('select', template);
const loadMore = () => {
  visibleCount.value += props.initialLimit;
};

watch([query, selectedCategory, selectedLanguage], () => {
  visibleCount.value = props.initialLimit;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="wa-template-gallery">
    <header class="wa-gallery-toolbar">
      <div class="wa-gallery-search">
        <Icon icon="i-lucide-search" class="size-4" />
        <input
          v-model="query"
          type="search"
          placeholder="Search by template name or message content"
          aria-label="Search WhatsApp templates"
        />
      </div>
      <button
        v-if="showRefresh"
        type="button"
        class="wa-refresh-button"
        :disabled="isRefreshing"
        title="Refresh WhatsApp templates"
        @click="emit('refresh')"
      >
        <Icon
          icon="i-lucide-refresh-ccw"
          class="size-4"
          :class="{ 'animate-spin': isRefreshing }"
        />
        <span>Refresh</span>
      </button>
    </header>

    <div class="wa-gallery-shell">
      <aside class="wa-gallery-sidebar">
        <section>
          <h3>Category</h3>
          <button
            v-for="option in categoryOptions"
            :key="option.value"
            type="button"
            class="wa-filter-option"
            :class="{ active: selectedCategory === option.value }"
            @click="selectedCategory = option.value"
          >
            <span class="wa-filter-radio" />
            <span>{{ option.label }}</span>
            <strong>{{ option.count }}</strong>
          </button>
        </section>

        <section v-if="languageOptions.length > 1">
          <h3>Language</h3>
          <button
            type="button"
            class="wa-filter-option"
            :class="{ active: selectedLanguage === 'all' }"
            @click="selectedLanguage = 'all'"
          >
            <span class="wa-filter-radio" />
            <span>All languages</span>
            <strong>{{ templates.length }}</strong>
          </button>
          <button
            v-for="option in languageOptions"
            :key="option.value"
            type="button"
            class="wa-filter-option"
            :class="{ active: selectedLanguage === option.value }"
            @click="selectedLanguage = option.value"
          >
            <span class="wa-filter-radio" />
            <span>{{ option.label }}</span>
            <strong>{{ option.count }}</strong>
          </button>
        </section>
      </aside>

      <main class="wa-gallery-results">
        <div class="wa-result-heading">
          <p>{{ resultLabel }}</p>
          <p>Select a card to review variables and send</p>
        </div>

        <div v-if="visibleTemplates.length" class="wa-template-grid">
          <article
            v-for="template in visibleTemplates"
            :key="template.id || `${template.name}-${template.language}`"
            class="wa-template-card"
            role="button"
            tabindex="0"
            :aria-label="`Select template ${template.name}`"
            @click="selectTemplate(template)"
            @keydown.enter.prevent="selectTemplate(template)"
            @keydown.space.prevent="selectTemplate(template)"
          >
            <div class="wa-card-meta">
              <div>
                <h4>{{ titleize(template.name) }}</h4>
                <p>{{ template.name }}</p>
              </div>
              <span>{{ template.language || 'en' }}</span>
            </div>

            <div class="wa-message-canvas">
              <div class="wa-message-bubble">
                <img
                  v-if="getMediaPreview(template)"
                  :src="getMediaPreview(template)"
                  :alt="`${template.name} media preview`"
                  class="wa-media-preview"
                />
                <div
                  v-else-if="isMediaHeader(template)"
                  class="wa-media-placeholder"
                >
                  <Icon icon="i-lucide-image" class="size-5" />
                  <span>{{ mediaHeaderLabel(template) }}</span>
                </div>
                <div class="wa-message-copy">
                  <p
                    v-if="getTemplateHeader(template)?.text"
                    class="wa-message-header"
                  >
                    {{ getTemplateHeader(template).text }}
                  </p>
                  <p class="wa-message-body" v-html="previewHtml(template)" />
                  <div class="wa-message-foot">
                    <span>{{ getTemplateFooter(template) }}</span>
                    <time>10:24</time>
                  </div>
                </div>
                <div
                  v-for="(button, index) in getTemplateButtons(template).slice(
                    0,
                    3
                  )"
                  :key="`${button.type}-${button.text}-${index}`"
                  class="wa-preview-button"
                >
                  <Icon :icon="buttonIcon(button.type)" class="size-4" />
                  <span>{{ button.text }}</span>
                </div>
                <div
                  v-if="getTemplateButtons(template).length > 3"
                  class="wa-preview-button"
                >
                  +{{ getTemplateButtons(template).length - 3 }} more
                </div>
              </div>
            </div>

            <footer class="wa-card-footer">
              <span
                class="wa-category-pill"
                :class="categoryClass(template.category)"
              >
                {{ titleize(template.category) }}
              </span>
              <span>Use template</span>
            </footer>
          </article>
        </div>

        <div v-else class="wa-gallery-empty">
          <Icon icon="i-lucide-search-x" class="size-7" />
          <h4>No templates found</h4>
          <p>Try another search, category, or language.</p>
        </div>

        <div
          v-if="visibleTemplates.length < filteredTemplates.length"
          class="wa-load-more"
        >
          <button type="button" @click="loadMore">Load more templates</button>
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped lang="scss">
.wa-template-gallery {
  @apply w-full overflow-hidden rounded-xl border border-n-weak bg-n-background;
}

.wa-gallery-toolbar {
  @apply flex items-center gap-3 border-b border-n-weak p-3;
}

.wa-gallery-search {
  @apply flex h-10 min-w-0 flex-1 items-center gap-2 rounded-lg bg-n-alpha-black2 px-3 text-n-slate-11 outline outline-1 outline-n-weak focus-within:outline-n-brand;

  input {
    @apply h-full min-w-0 flex-1 border-0 bg-transparent text-sm text-n-slate-12 outline-none;
  }
}

.wa-refresh-button {
  @apply flex h-10 items-center gap-2 rounded-lg border border-n-weak px-3 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-wait disabled:opacity-50;
}

.wa-gallery-shell {
  display: grid;
  grid-template-columns: 13rem minmax(0, 1fr);
  height: min(68vh, 46rem);
  min-height: 32rem;
}

.wa-gallery-sidebar {
  @apply overflow-y-auto border-r border-n-weak bg-n-alpha-black2 p-4;

  section + section {
    @apply mt-6 border-t border-n-weak pt-5;
  }

  h3 {
    @apply mb-2 text-xs font-semibold uppercase tracking-wide text-n-slate-11;
  }
}

.wa-filter-option {
  @apply mb-1 flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12;

  strong {
    @apply ml-auto text-xs font-medium;
  }

  &.active {
    @apply bg-n-alpha-2 font-medium text-n-slate-12;

    .wa-filter-radio {
      @apply border-n-brand bg-n-brand shadow-[inset_0_0_0_3px_white];
    }
  }
}

.wa-filter-radio {
  @apply size-4 shrink-0 rounded-full border border-n-strong bg-n-background;
}

.wa-gallery-results {
  @apply overflow-y-auto bg-n-alpha-1 p-4;
}

.wa-result-heading {
  @apply mb-4 flex items-center justify-between gap-4 text-xs text-n-slate-11;

  p:last-child {
    @apply hidden sm:block;
  }
}

.wa-template-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(17rem, 1fr));
  gap: 1rem;
}

.wa-template-card {
  @apply flex min-h-[27rem] cursor-pointer flex-col overflow-hidden rounded-xl border border-n-weak bg-n-background shadow-sm transition hover:-translate-y-0.5 hover:border-n-brand hover:shadow-md focus:outline-none focus:ring-2 focus:ring-n-brand;
}

.wa-card-meta {
  @apply flex min-h-[4.5rem] items-start justify-between gap-3 border-b border-n-weak px-3 py-3;

  h4 {
    @apply line-clamp-1 text-sm font-semibold text-n-slate-12;
  }

  p {
    @apply mt-0.5 line-clamp-1 text-[11px] text-n-slate-10;
  }

  > span {
    @apply shrink-0 rounded-full bg-n-alpha-2 px-2 py-1 text-[10px] font-semibold uppercase text-n-slate-11;
  }
}

.wa-message-canvas {
  @apply flex flex-1 items-start bg-[#efeae2] p-4 dark:bg-n-slate-4;
  background-image: radial-gradient(
    rgba(100, 80, 60, 0.08) 0.8px,
    transparent 0.8px
  );
  background-size: 12px 12px;
}

.wa-message-bubble {
  @apply w-full overflow-hidden rounded-lg bg-white text-slate-900 shadow-sm;
}

.wa-media-preview {
  @apply max-h-36 w-full object-cover;
}

.wa-media-placeholder {
  @apply grid h-24 place-content-center justify-items-center gap-1 bg-slate-100 text-xs font-medium text-slate-500;
}

.wa-message-copy {
  @apply px-3 pb-2 pt-3;
}

.wa-message-header {
  @apply mb-1 whitespace-pre-wrap text-sm font-semibold;
}

.wa-message-body {
  @apply line-clamp-[9] whitespace-pre-wrap text-[13px] leading-[1.45];
}

:deep(.wa-template-variable) {
  @apply font-medium text-n-teal-11;
}

.wa-message-foot {
  @apply mt-2 flex items-end justify-between gap-2 text-[10px] text-slate-400;

  span {
    @apply line-clamp-2;
  }

  time {
    @apply ml-auto shrink-0;
  }
}

.wa-preview-button {
  @apply flex min-h-10 items-center justify-center gap-2 border-t border-slate-200 px-3 text-center text-xs font-medium text-n-teal-11;
}

.wa-card-footer {
  @apply flex min-h-12 items-center justify-between gap-3 border-t border-n-weak px-3 py-2 text-xs font-medium text-n-brand;
}

.wa-category-pill {
  @apply rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-wide;

  &.is-utility {
    @apply bg-n-blue-2 text-n-blue-11;
  }

  &.is-authentication {
    @apply bg-n-iris-2 text-n-iris-11;
  }

  &.is-marketing {
    @apply bg-n-amber-2 text-n-amber-11;
  }

  &.is-default {
    @apply bg-slate-100 text-slate-600;
  }
}

.wa-gallery-empty {
  @apply grid min-h-72 place-content-center justify-items-center gap-2 text-center text-n-slate-11;

  h4 {
    @apply text-sm font-semibold text-n-slate-12;
  }

  p {
    @apply text-xs;
  }
}

.wa-load-more {
  @apply flex justify-center py-5;

  button {
    @apply rounded-lg border border-n-weak bg-n-background px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2;
  }
}

@media (max-width: 760px) {
  .wa-gallery-shell {
    grid-template-columns: 1fr;
    height: min(72vh, 42rem);
  }

  .wa-gallery-sidebar {
    @apply flex gap-2 overflow-x-auto border-b border-r-0 p-3;

    section {
      @apply flex shrink-0 items-center gap-1;
    }

    section + section {
      @apply mt-0 border-l border-t-0 pl-3 pt-0;
    }

    h3 {
      @apply mb-0 mr-1;
    }

    .wa-filter-option {
      @apply mb-0 w-auto shrink-0;
    }
  }

  .wa-template-grid {
    grid-template-columns: 1fr;
  }

  .wa-refresh-button span {
    @apply hidden;
  }
}
</style>
